import React from "react";
import { useTranslation } from "react-i18next";
import { Sparkles } from "lucide-react";
import { OrderGroupDetail } from "@/api/models/api-types";
import { formatPrice, formatCredit } from "../utils/format-price";
import { PaymentBrandLogo } from "@/features/settings/components/PaymentBrandLogo";

interface OrderDetailAddressPaymentSectionProps {
  orderGroupDetail?: OrderGroupDetail;
  currency: string;
}

export const OrderDetailAddressPaymentSection: React.FC<
  OrderDetailAddressPaymentSectionProps
> = ({ orderGroupDetail, currency }) => {
  const { t } = useTranslation();

  const totalAmount = orderGroupDetail?.totalAmount ?? 0;
  const shipping = orderGroupDetail?.shippingCost ?? 0;
  const subtotal = orderGroupDetail?.subTotal ?? (totalAmount - shipping);
  const cashback = orderGroupDetail?.cashbackAmount ?? 0;

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
      {/* Delivery Address Card */}
      <div className="border border-border-primary dark:border-gray-700 rounded-[12px] p-2.5 md:p-3">
        <div className="flex flex-col gap-2 md:gap-3">
          <div className="flex items-center justify-between gap-2">
            <p className="text-xs md:text-sm font-medium text-text-quaternary dark:text-gray-400">
              {t("orders.detail.deliveryAddress")}
            </p>
            <p className="text-xs md:text-sm font-semibold text-text-primary dark:text-gray-100">
              {orderGroupDetail?.shippingAddress?.label || "Address"}
            </p>
          </div>
          <div className="h-px bg-border-primary dark:bg-gray-700" />
          <div className="flex flex-col gap-1.5 md:gap-2">
            {/* First Name & Last Name */}
            {(orderGroupDetail?.shippingAddress?.firstName ||
              orderGroupDetail?.shippingAddress?.lastName) && (
              <p className="text-xs md:text-sm font-bold text-text-primary dark:text-gray-100">
                {orderGroupDetail?.shippingAddress?.firstName}{" "}
                {orderGroupDetail?.shippingAddress?.lastName}
              </p>
            )}

            <div className="text-xs md:text-sm text-text-primary font-thin dark:text-gray-100 space-y-0.5">
              <p className="break-words">
                {orderGroupDetail?.shippingAddress?.addressLine1}
              </p>
              {orderGroupDetail?.shippingAddress?.addressLine2 && (
                <p className="break-words">
                  {orderGroupDetail?.shippingAddress?.addressLine2}
                </p>
              )}
              <p className="break-words">
                {orderGroupDetail?.shippingAddress?.city},{" "}
                {orderGroupDetail?.shippingAddress?.state}{" "}
                {orderGroupDetail?.shippingAddress?.postalCode}
              </p>
            </div>
            <p className="text-xs md:text-sm text-text-primary dark:text-gray-100">
              {orderGroupDetail?.shippingAddress?.country}
            </p>
          </div>
        </div>
      </div>

      {/* Invoice Address Card */}
      <div className="border border-border-primary dark:border-gray-700 rounded-[12px] p-2.5 md:p-3">
        <div className="flex flex-col gap-2 md:gap-3">
          <div className="flex items-center justify-between gap-2">
            <p className="text-xs md:text-sm font-medium text-text-quaternary dark:text-gray-400">
              {t("orders.detail.invoiceAddress")}
            </p>
            <p className="text-xs md:text-sm font-semibold text-text-primary dark:text-gray-100">
              {orderGroupDetail?.billingAddress?.label || "Address"}
            </p>
          </div>
          <div className="h-px bg-border-primary dark:bg-gray-700" />
          <div className="flex flex-col gap-1.5 md:gap-2">
            {/* First Name & Last Name */}
            {(orderGroupDetail?.billingAddress?.firstName ||
              orderGroupDetail?.billingAddress?.lastName) && (
              <p className="text-xs md:text-sm font-medium text-text-primary dark:text-gray-100">
                {orderGroupDetail?.billingAddress?.firstName}{" "}
                {orderGroupDetail?.billingAddress?.lastName}
              </p>
            )}

            <div className="text-xs md:text-sm text-text-primary font-thin dark:text-gray-100 space-y-0.5">
              <p className="break-words">
                {orderGroupDetail?.billingAddress?.addressLine1}
              </p>
              {orderGroupDetail?.billingAddress?.addressLine2 && (
                <p className="break-words">
                  {orderGroupDetail?.billingAddress?.addressLine2}
                </p>
              )}
              <p className="break-words">
                {orderGroupDetail?.billingAddress?.city},{" "}
                {orderGroupDetail?.billingAddress?.state}{" "}
                {orderGroupDetail?.billingAddress?.postalCode}
              </p>
            </div>
            <p className="text-xs md:text-sm text-text-primary dark:text-gray-100">
              {orderGroupDetail?.billingAddress?.country}
            </p>
          </div>
        </div>
      </div>

      {/* Payment Information Card */}
      <div className="border border-border-primary dark:border-gray-700 rounded-[12px] p-2.5 md:p-3">
        <div className="flex flex-col gap-2 md:gap-3">
          <p className="text-xs md:text-sm font-medium text-text-quaternary dark:text-gray-400">
            {t("orders.detail.paymentInformation")}
          </p>
          <div className="h-px bg-border-primary dark:bg-gray-700" />
          <div className="flex flex-col gap-1.5 md:gap-2">
            {/* Payment Method */}
            <div className="flex items-center justify-between gap-2">
              <p className="text-xs md:text-sm font-medium text-text-primary dark:text-gray-100">
                {t("orders.detail.payment")}
              </p>
              <div className="flex items-center gap-1.5 md:gap-2 flex-shrink-0">
                {orderGroupDetail?.cardAssociation && (
                  <PaymentBrandLogo
                    brand={orderGroupDetail.cardAssociation}
                    className="w-8 h-5 md:w-10 md:h-6"
                  />
                )}
                <span className="text-[10px] md:text-xs font-medium text-text-primary dark:text-gray-100">
                  •••• {orderGroupDetail?.lastDigits || "0000"}
                </span>
              </div>
            </div>

            {/* Subtotal */}
            <div className="flex items-center justify-between gap-2">
              <p className="text-xs md:text-sm font-medium text-text-primary dark:text-gray-100">
                {t("orders.detail.subtotal")}
              </p>
              <p className="text-xs md:text-sm font-semibold text-text-primary dark:text-gray-100">
                {formatPrice(subtotal, currency)}
              </p>
            </div>

            {/* Shipping */}
            <div className="flex items-center justify-between gap-2">
              <p className="text-xs md:text-sm font-medium text-text-primary dark:text-gray-100">
                {t("orders.detail.shipping")}
              </p>
              <p className={`text-xs md:text-sm font-semibold ${shipping === 0 ? "text-[#17b26a]" : "text-text-primary dark:text-gray-100"}`}>
                {shipping === 0 ? t("orders.freeShipping") : formatPrice(shipping, currency)}
              </p>
            </div>

            {/* Divider */}
            <div className="h-px bg-border-primary dark:bg-gray-700" />

            {/* Total */}
            <div className="flex items-center justify-between gap-2">
              <p className="text-xs md:text-sm font-medium text-text-primary dark:text-gray-100">
                {t("orders.detail.total")}
              </p>
              <p className="text-xs md:text-sm font-semibold text-text-primary dark:text-gray-100">
                {formatPrice(totalAmount, currency)}
              </p>
            </div>

            {/* Cashback */}
            {cashback > 0 && (
              <div className="flex items-center justify-between gap-2 bg-[#f0fdf4] dark:bg-green-950/30 rounded-lg px-2.5 py-2 mt-0.5">
                <div className="flex items-center gap-1.5">
                  <Sparkles className="w-3.5 h-3.5 text-[#17b26a] flex-shrink-0" />
                  <p className="text-[10px] md:text-xs font-medium text-[#17b26a]">
                    {t("orders.cashbackEarned")}
                  </p>
                </div>
                <p className="text-[10px] md:text-xs font-bold text-[#17b26a]">
                  +{formatCredit(cashback)}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
